import { useState, useEffect } from 'react';
import { AuthProvider } from './components/auth/auth-provider';
import { AuthForm } from './components/auth/auth-form';
import { TaskBoard } from './components/tasks/task-board';
import { OwnersPage } from './pages/owners';
import { ScrumBoard } from './components/scrum/scrum-board';
import { MeetingsPage } from './pages/meetings';
import { CompletedMeetingsPage } from './pages/completed-meetings';
import { AdminPage } from './pages/admin';
import { useAuth } from './lib/auth';
import { Header } from './components/Header';
import { type Page } from './types/auth';
import { QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { queryClient } from './lib/cache';
import { AppContext } from './lib/app-context';

function AppContent() {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState<Page>(() => {
    // Try to get saved page from localStorage, default to 'tasks' if not found
    const savedPage = localStorage.getItem('currentPage');
    return (savedPage as Page) || 'tasks';
  });

  // Save page to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('currentPage', currentPage);
  }, [currentPage]);

  useEffect(() => {
    const handleShowOwnersPage = () => setCurrentPage('owners');
    window.addEventListener('showOwnersPage', handleShowOwnersPage);
    return () => window.removeEventListener('showOwnersPage', handleShowOwnersPage);
  }, []);

  if (!user) {
    return <AuthForm />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'tasks':
        return <TaskBoard />;
      case 'owners':
        return <OwnersPage />;
      case 'scrum':
        return <ScrumBoard />;
      case 'meetings':
        return <MeetingsPage />;
      case 'completed':
        return <CompletedMeetingsPage />;
      case 'admin':
        return <AdminPage />;
      default:
        return <TaskBoard />;
    }
  };

  return (
    <AppContext.Provider value={{ currentPage, setCurrentPage }}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
        <Header currentPage={currentPage} onPageChange={setCurrentPage} />
        <main className="container mx-auto px-4 py-8">
          {renderPage()}
        </main>
      </div>
    </AppContext.Provider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppContent />
        <Toaster position="top-right" />
      </AuthProvider>
    </QueryClientProvider>
  );
}